// Zastepstwo dla oryginalnego excalidraw-app/data/firebase.ts.
// Ten plik ma DOKLADNIE TE SAME nazwy eksportow, wiec Collab.tsx i inne
// miejsca importujace z "./data/firebase" dzialaja bez zadnych zmian.
//
// Kluczowa roznica: zamiast Firestore (limit 1MB/dokument) uzywamy
// wlasnego API (Node + Postgres), bez takiego limitu. Szyfrowanie
// end-to-end pozostaje identyczne jak w oryginale - serwer nigdy nie
// widzi tresci rysunku, tylko zaszyfrowane bajty.

import { reconcileElements } from "@excalidraw/excalidraw";
import { MIME_TYPES, toBrandedType } from "@excalidraw/common";
import { decompressData } from "@excalidraw/excalidraw/data/encode";
import {
  encryptData,
  decryptData,
} from "@excalidraw/excalidraw/data/encryption";
import { restoreElements } from "@excalidraw/excalidraw/data/restore";
import { getSceneVersion } from "@excalidraw/element";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type {
  ExcalidrawElement,
  FileId,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  BinaryFileData,
  BinaryFileMetadata,
  DataURL,
} from "@excalidraw/excalidraw/types";

import { getSyncableElements } from ".";

import type { SyncableExcalidrawElement } from ".";
import type Portal from "../collab/Portal";
import type { Socket } from "socket.io-client";

// -----------------------------------------------------------------------------

const STORAGE_BACKEND_URL: string =
  import.meta.env.VITE_APP_STORAGE_BACKEND_URL || "http://localhost:8080";

type StoredScene = {
  sceneVersion: number;
  ciphertext: string; // base64
  iv: string; // base64
};

const encryptElements = async (
  key: string,
  elements: readonly ExcalidrawElement[],
): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> => {
  const json = JSON.stringify(elements);
  const encoded = new TextEncoder().encode(json);
  const { encryptedBuffer, iv } = await encryptData(key, encoded);
  return { ciphertext: encryptedBuffer, iv };
};

const decryptElements = async (
  data: StoredScene,
  roomKey: string,
): Promise<readonly ExcalidrawElement[]> => {
  const ciphertext = base64ToUint8Array(
    data.ciphertext,
  ) as Uint8Array<ArrayBuffer>;
  const iv = base64ToUint8Array(data.iv) as Uint8Array<ArrayBuffer>;

  const decrypted = await decryptData(iv, ciphertext, roomKey);
  const decodedData = new TextDecoder("utf-8").decode(
    new Uint8Array(decrypted),
  );
  return JSON.parse(decodedData);
};

const bufferToBase64 = (buffer: ArrayBuffer | Uint8Array): string => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const base64ToUint8Array = (base64: string): Uint8Array => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const createStoredScene = async (
  elements: readonly SyncableExcalidrawElement[],
  roomKey: string,
): Promise<StoredScene> => {
  const sceneVersion = getSceneVersion(elements);
  const { ciphertext, iv } = await encryptElements(roomKey, elements);
  return {
    sceneVersion,
    ciphertext: bufferToBase64(ciphertext),
    iv: bufferToBase64(iv),
  };
};

class SceneVersionCache {
  private static cache = new WeakMap<Socket, number>();
  static get = (socket: Socket) => {
    return SceneVersionCache.cache.get(socket);
  };
  static set = (
    socket: Socket,
    elements: readonly SyncableExcalidrawElement[],
  ) => {
    SceneVersionCache.cache.set(socket, getSceneVersion(elements));
  };
}

export const isSavedToFirebase = (
  portal: Portal,
  elements: readonly ExcalidrawElement[],
): boolean => {
  if (portal.socket && portal.roomId && portal.roomKey) {
    const sceneVersion = getSceneVersion(elements);
    return SceneVersionCache.get(portal.socket) === sceneVersion;
  }
  return true;
};

// Uzywane tylko przez funkcje "Export to Excalidraw+" (osobny, platny
// produkt firmy Excalidraw, niezwiazany z self-hostowana kolaboracja).
// Zostawiamy jako no-op (rzutowanie na any, zeby nie psuc typow w miejscu
// wywolania), zeby caly plik nadal sie kompilowal.
export const loadFirebaseStorage = async () => {
  return null as any;
};

const fetchScene = async (roomId: string): Promise<StoredScene | null> => {
  const response = await fetch(
    `${STORAGE_BACKEND_URL}/api/v1/scenes/${roomId}`,
  );
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch scene: ${response.status}`);
  }
  return response.json();
};

const putScene = async (
  roomId: string,
  baseVersion: number | null,
  storedScene: StoredScene,
): Promise<{ ok: boolean; scene: StoredScene }> => {
  const response = await fetch(
    `${STORAGE_BACKEND_URL}/api/v1/scenes/${roomId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ baseVersion, ...storedScene }),
    },
  );
  const body = await response.json();
  return { ok: response.ok, scene: body };
};

export const saveFilesToFirebase = async ({
  prefix,
  files,
}: {
  prefix: string;
  files: { id: FileId; buffer: Uint8Array }[];
}) => {
  const erroredFiles: FileId[] = [];
  const savedFiles: FileId[] = [];

  await Promise.all(
    files.map(async ({ id, buffer }) => {
      try {
        const response = await fetch(
          `${STORAGE_BACKEND_URL}/api/v1/files/${encodeURIComponent(
            prefix,
          )}/${id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/octet-stream" },
            body: buffer as BodyInit,
          },
        );
        if (!response.ok) {
          throw new Error(`upload failed: ${response.status}`);
        }
        savedFiles.push(id);
      } catch (error: any) {
        erroredFiles.push(id);
      }
    }),
  );

  return { savedFiles, erroredFiles };
};

const MAX_RETRIES = 5;

// Odpowiednik oryginalnej transakcji Firestore (get + reconcile + set,
// atomowo). Tutaj robimy to jako petle "optimistic concurrency": czytamy
// aktualny stan, reconcilujemy lokalnie (bo tylko klient ma klucz do
// odszyfrowania), probujemy zapisac z warunkiem "tylko jesli nikt inny
// nie zapisal w miedzyczasie" (baseVersion). Jesli ktos nas ubiegl (409),
// powtarzamy cala operacje ze swiezymi danymi.
export const saveToFirebase = async (
  portal: Portal,
  elements: readonly SyncableExcalidrawElement[],
  appState: AppState,
) => {
  const { roomId, roomKey, socket } = portal;
  if (!roomId || !roomKey || !socket || isSavedToFirebase(portal, elements)) {
    return null;
  }

  let finalStoredScene: StoredScene | null = null;

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    const current = await fetchScene(roomId);

    let storedScene: StoredScene;
    let baseVersion: number | null;

    if (!current) {
      storedScene = await createStoredScene(elements, roomKey);
      baseVersion = null;
    } else {
      const prevElements = getSyncableElements(
        restoreElements(await decryptElements(current, roomKey), null),
      );
      const reconciledElements = getSyncableElements(
        reconcileElements(
          elements,
          prevElements as OrderedExcalidrawElement[] as RemoteExcalidrawElement[],
          appState,
        ),
      );
      storedScene = await createStoredScene(reconciledElements, roomKey);
      baseVersion = current.sceneVersion;
    }

    const { ok, scene } = await putScene(roomId, baseVersion, storedScene);
    if (ok) {
      finalStoredScene = scene;
      break;
    }
    // 409 - ktos inny zapisal w miedzyczasie, sprobujmy ponownie z jego
    // wersja jako punktem wyjscia w kolejnej iteracji petli.
  }

  if (!finalStoredScene) {
    throw new Error(
      "Nie udalo sie zapisac sceny po kilku probach (za duzy ruch w pokoju).",
    );
  }

  const storedElements = getSyncableElements(
    restoreElements(await decryptElements(finalStoredScene, roomKey), null),
  );

  SceneVersionCache.set(socket, storedElements);

  return toBrandedType<RemoteExcalidrawElement[]>(storedElements);
};

export const loadFromFirebase = async (
  roomId: string,
  roomKey: string,
  socket: Socket | null,
): Promise<readonly SyncableExcalidrawElement[] | null> => {
  const storedScene = await fetchScene(roomId);
  if (!storedScene) {
    return null;
  }

  const elements = getSyncableElements(
    restoreElements(await decryptElements(storedScene, roomKey), null, {
      deleteInvisibleElements: true,
    }),
  );

  if (socket) {
    SceneVersionCache.set(socket, elements);
  }

  return elements;
};

export const loadFilesFromFirebase = async (
  prefix: string,
  decryptionKey: string,
  filesIds: readonly FileId[],
) => {
  const loadedFiles: BinaryFileData[] = [];
  const erroredFiles = new Map<FileId, true>();

  await Promise.all(
    [...new Set(filesIds)].map(async (id) => {
      try {
        const url = `${STORAGE_BACKEND_URL}/api/v1/files/${encodeURIComponent(
          prefix,
        )}/${id}`;
        const response = await fetch(url);
        if (response.status < 400) {
          const arrayBuffer = await response.arrayBuffer();

          const { data, metadata } = await decompressData<BinaryFileMetadata>(
            new Uint8Array(arrayBuffer),
            { decryptionKey },
          );

          const dataURL = new TextDecoder().decode(data) as DataURL;

          loadedFiles.push({
            mimeType: metadata.mimeType || MIME_TYPES.binary,
            id,
            dataURL,
            created: metadata?.created || Date.now(),
            lastRetrieved: metadata?.created || Date.now(),
          });
        } else {
          erroredFiles.set(id, true);
        }
      } catch (error: any) {
        erroredFiles.set(id, true);
        console.error(error);
      }
    }),
  );

  return { loadedFiles, erroredFiles };
};